// Pure preview model builder for expanded subprocess nodes.

function numOrZero(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function defaultGetNodeData(node) {
    return node?.data || {};
}

function defaultGetNodeTypeFromData(data) {
    return data?.nodeType || (data?.motionId ? "motion" : "");
}

function defaultResolveNodeLabel(data, node = null) {
    return data?.label || data?.subprocessName || data?.conditionText || data?.signalName || node?.name || "ノード";
}

function buildPreviewConnections(sortedNodes, nodeIds) {
    const connections = [];
    for (const rawNode of sortedNodes) {
        const sourceId = String(rawNode?.id || "");
        if (!nodeIds.has(sourceId)) continue;
        for (const output of Object.values(rawNode?.outputs || {})) {
            for (const connection of output?.connections || []) {
                const targetId = String(connection?.node || "");
                if (!nodeIds.has(targetId)) continue;
                connections.push({ sourceId, targetId });
            }
        }
    }
    return connections;
}

function buildAutoLayoutLayers(sourceNodes, connections) {
    const nodeIds = new Set(sourceNodes.map((node) => node.id));
    const indegree = new Map(sourceNodes.map((node) => [node.id, 0]));
    const outgoing = new Map(sourceNodes.map((node) => [node.id, []]));
    for (const connection of connections) {
        if (!nodeIds.has(connection.sourceId) || !nodeIds.has(connection.targetId)) continue;
        outgoing.get(connection.sourceId).push(connection.targetId);
        indegree.set(connection.targetId, (indegree.get(connection.targetId) || 0) + 1);
    }

    const sourceOrder = new Map(sourceNodes.map((node, index) => [node.id, index]));
    const queue = sourceNodes
        .filter((node) => (indegree.get(node.id) || 0) === 0)
        .map((node) => node.id);
    const layerById = new Map(sourceNodes.map((node) => [node.id, 0]));
    const visited = new Set();

    while (queue.length) {
        queue.sort((a, b) => (sourceOrder.get(a) || 0) - (sourceOrder.get(b) || 0));
        const sourceId = queue.shift();
        visited.add(sourceId);
        for (const targetId of outgoing.get(sourceId) || []) {
            layerById.set(targetId, Math.max(layerById.get(targetId) || 0, (layerById.get(sourceId) || 0) + 1));
            indegree.set(targetId, (indegree.get(targetId) || 0) - 1);
            if ((indegree.get(targetId) || 0) === 0) queue.push(targetId);
        }
    }

    for (const node of sourceNodes) {
        if (!visited.has(node.id)) {
            const incoming = connections.filter((connection) => connection.targetId === node.id);
            const fallbackLayer = incoming.length
                ? Math.max(...incoming.map((connection) => (layerById.get(connection.sourceId) || 0) + 1))
                : 0;
            layerById.set(node.id, fallbackLayer);
        }
    }

    const layers = [];
    for (const node of sourceNodes) {
        const layerIndex = Math.max(0, layerById.get(node.id) || 0);
        if (!layers[layerIndex]) layers[layerIndex] = [];
        layers[layerIndex].push(node);
    }
    return layers.filter(Boolean);
}

export function buildSubprocessPreviewState(
    {
        moduleName = "",
        moduleData = {},
        getNodeData = defaultGetNodeData,
        getNodeTypeFromData = defaultGetNodeTypeFromData,
        resolveNodeLabel = defaultResolveNodeLabel
    } = {}
) {
    const sortedNodes = Object.values(moduleData || {})
        .sort((a, b) => (Number(a?.pos_x) - Number(b?.pos_x)) || (Number(a?.pos_y) - Number(b?.pos_y)) || (Number(a?.id) - Number(b?.id)));
    if (!sortedNodes.length) {
        return {
            moduleName,
            nodes: [],
            edges: [],
            canvasWidth: 160,
            canvasHeight: 76
        };
    }

    const sourceNodes = sortedNodes.map((node) => {
        const data = getNodeData(node);
        return {
            id: String(node?.id || ""),
            type: getNodeTypeFromData(data) || "generic",
            label: resolveNodeLabel(data, node),
            orderX: numOrZero(node?.pos_x),
            orderY: numOrZero(node?.pos_y)
        };
    });

    const previewNodeWidth = 56;
    const previewNodeHeight = 22;
    const paddingX = 16;
    const paddingY = 18;
    const columnGap = 28;
    const rowGap = 16;
    const nodeIds = new Set(sourceNodes.map((node) => node.id));
    const connections = buildPreviewConnections(sortedNodes, nodeIds);
    const layers = buildAutoLayoutLayers(sourceNodes, connections);
    const maxLayerRows = Math.max(...layers.map((layer) => layer.length));
    const canvasHeight = Math.max(138, (paddingY * 2) + (maxLayerRows * previewNodeHeight) + ((maxLayerRows - 1) * rowGap));

    const previewNodes = [];
    for (const [layerIndex, layer] of layers.entries()) {
        const orderedLayer = [...layer].sort((a, b) => (a.orderY - b.orderY) || (a.orderX - b.orderX) || a.id.localeCompare(b.id));
        const layerHeight = (orderedLayer.length * previewNodeHeight) + ((orderedLayer.length - 1) * rowGap);
        const topStart = (canvasHeight - layerHeight) / 2;
        for (const [rowIndex, node] of orderedLayer.entries()) {
            previewNodes.push({
                id: node.id,
                type: node.type,
                label: node.label,
                width: previewNodeWidth,
                height: previewNodeHeight,
                left: paddingX + (layerIndex * (previewNodeWidth + columnGap)),
                top: topStart + (rowIndex * (previewNodeHeight + rowGap))
            });
        }
    }
    const nodeById = new Map(previewNodes.map((node) => [node.id, node]));
    const previewEdges = connections.map((connection) => {
        const sourcePreview = nodeById.get(connection.sourceId);
        const targetPreview = nodeById.get(connection.targetId);
        if (!sourcePreview || !targetPreview) return null;
        return {
            x1: sourcePreview.left + sourcePreview.width,
            y1: sourcePreview.top + (sourcePreview.height / 2),
            x2: targetPreview.left,
            y2: targetPreview.top + (targetPreview.height / 2)
        };
    }).filter(Boolean);

    const canvasWidth = Math.max(...previewNodes.map((node) => node.left + node.width)) + paddingX;
    return {
        moduleName,
        nodes: previewNodes,
        edges: previewEdges,
        canvasWidth,
        canvasHeight
    };
}
